import { useState, useEffect, useCallback, useRef } from "react";
import type { Drop, Sale, KIState, PriceEntry, Player, ENM } from "./data";
import { genId, kiKey, PLAYERS, ENM_DATA } from "./data";

// ─── Generic fetch helper ────────────────────────────────────────────────────

async function api<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res.json();
}

// ─── Data hooks ──────────────────────────────────────────────────────────────

export function useDrops(player: Player) {
  // Ensure a realtime connection exists. Side-effect only — no re-renders
  // on connection/presence state changes (we only care about data:* events,
  // which we subscribe to via subscribeRealtime below).
  useRealtimeReady(player);

  const [drops, setDrops] = useState<Drop[]>([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    const data = await api<Drop[]>("drops");
    setDrops(data);
    setLoaded(true);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Merge inbound realtime drop/delete events from peers (and our own
  // server-broadcasted commits). Dedup against any optimistic row.
  useEffect(() => {
    return subscribeRealtime((e) => {
      if (e.type === "data:drop") {
        setDrops(prev => prev.some(d => d.id === e.drop.id) ? prev : [e.drop, ...prev]);
      } else if (e.type === "data:delete" && e.kind === "drop") {
        setDrops(prev => prev.filter(d => d.id !== e.id));
      }
    });
  }, []);

  // Recover events that arrived during a disconnect: when the realtime
  // connection transitions false → true, do a one-shot REST refresh.
  // The wasConnected === false guard skips initial mount (null → true),
  // which the mount-time refresh above already covers.
  useEffect(() => {
    let wasConnected: boolean | null = null;
    return subscribeRealtimeConn((c) => {
      if (wasConnected === false && c) refresh();
      wasConnected = c;
    });
  }, [refresh]);

  const logRun = useCallback(async (enm: ENM, items: string[], runNumber: number) => {
    const id = genId();
    const drop: Drop = { id, enm_id: enm.id, run_date: new Date().toISOString(), items, run_number: runNumber };
    // Optimistic update
    setDrops(prev => [drop, ...prev]);
    const res = await api<{ ok: boolean; id: string; deduped?: boolean }>(
      "drops",
      { method: "POST", body: JSON.stringify({ id, enm_id: enm.id, items, run_number: runNumber }) }
    );
    // Reconcile optimistic id with server-returned id when the backend
    // dedups (returns an existing row's id). Avoid duplicating if the
    // realtime broadcast already inserted the canonical row.
    if (res.id && res.id !== id) {
      setDrops(prev => {
        const without = prev.filter(d => d.id !== id);
        return without.some(d => d.id === res.id) ? without : [{ ...drop, id: res.id }, ...without];
      });
    }
    // Consume the group KI for both players. The server wipes steps, clears
    // needs_run, and anchors the 5-day window at the run when no countdown is
    // already in flight (acquisition-triggered ENMs anchored it earlier, at
    // final-step check — the DB guard preserves that). enm_id rides along for
    // back-compat with pre-group server code.
    await api("ki/consume", { method: "POST", body: JSON.stringify({ ki_key: kiKey(enm), enm_id: enm.id }) });
  }, []);

  const deleteDrop = useCallback(async (id: string) => {
    setDrops(prev => prev.filter(d => d.id !== id));
    await api(`drops/${id}`, { method: "DELETE" });
  }, []);

  return { drops, loaded, logRun, deleteDrop, refresh };
}

export function useSales(player: Player) {
  // Ensure a realtime connection exists. Side-effect only — no re-renders
  // on connection/presence state changes (we only care about data:* events,
  // which we subscribe to via subscribeRealtime below).
  useRealtimeReady(player);

  const [sales, setSales] = useState<Sale[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<{ imported: number; candidates: number; items_checked?: number; ah_rows_new?: number } | null>(null);

  const refresh = useCallback(async () => {
    const data = await api<Sale[]>("sales");
    setSales(data);
    setLoaded(true);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Merge inbound realtime sale/delete events from peers (and our own
  // server-broadcasted commits). Dedup against any optimistic row.
  useEffect(() => {
    return subscribeRealtime((e) => {
      if (e.type === "data:sale") {
        setSales(prev => prev.some(s => s.id === e.sale.id) ? prev : [e.sale, ...prev]);
      } else if (e.type === "data:delete" && e.kind === "sale") {
        setSales(prev => prev.filter(s => s.id !== e.id));
      }
    });
  }, []);

  // Recover events that arrived during a disconnect: when the realtime
  // connection transitions false → true, do a one-shot REST refresh.
  useEffect(() => {
    let wasConnected: boolean | null = null;
    return subscribeRealtimeConn((c) => {
      if (wasConnected === false && c) refresh();
      wasConnected = c;
    });
  }, [refresh]);

  const logSale = useCallback(async (enmId: string, itemName: string, price: number) => {
    const id = genId();
    const sale: Sale = { id, enm_id: enmId, item_name: itemName, price, sale_date: new Date().toISOString(), seller: "Donpeyote", source: "manual" };
    setSales(prev => [sale, ...prev]);
    await api("sales", { method: "POST", body: JSON.stringify({ id, enm_id: enmId, item_name: itemName, price, seller: "Donpeyote" }) });
  }, []);

  const deleteSale = useCallback(async (id: string) => {
    setSales(prev => prev.filter(s => s.id !== id));
    await api(`sales/${id}`, { method: "DELETE" });
  }, []);

  const syncFromAH = useCallback(async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      const res = await api<{ ok: boolean; imported: number; candidates: number; items_checked?: number; ah_rows_new?: number }>("sales/sync", { method: "POST" });
      setSyncResult({ imported: res.imported, candidates: res.candidates, items_checked: res.items_checked, ah_rows_new: res.ah_rows_new });
      await refresh();
    } catch (e: any) {
      setSyncError(e?.message || "Sales sync failed");
      setSyncResult(null);
    } finally {
      setSyncing(false);
    }
  }, [refresh]);

  return { sales, loaded, logSale, deleteSale, refresh, syncFromAH, syncing, syncError, syncResult };
}

export function useKIState() {
  const [kiState, setKiState] = useState<Record<string, KIState>>({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    const data = await api<Record<string, KIState>>("ki");
    setKiState(data);
    setLoaded(true);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Merge inbound realtime KI events. The server broadcasts data:ki with the
  // canonical post-write rows after every ki PUT and ki/consume — this is what
  // keeps a tab that did NOT log the run showing the new cooldown instead of
  // stale "Holding KI" until the next full reload. (The realtime connection
  // itself is opened by the App root via useRealtimeReady; this hook only
  // subscribes to the singleton.)
  useEffect(() => {
    return subscribeRealtime((e) => {
      if (e.type === "data:ki") {
        setKiState(prev => ({ ...prev, ...e.state }));
      }
    });
  }, []);

  // Recover KI events that arrived during a disconnect: same false → true
  // one-shot refresh pattern as useDrops / useSales.
  useEffect(() => {
    let wasConnected: boolean | null = null;
    return subscribeRealtimeConn((c) => {
      if (wasConnected === false && c) refresh();
      wasConnected = c;
    });
  }, [refresh]);

  const getKS = useCallback((kiKeyStr: string, player: string): KIState => {
    return kiState[`${kiKeyStr}-${player}`] || { steps: {}, acquiredAt: null, needsRun: false };
  }, [kiState]);

  const updateKI = useCallback(async (kiKeyStr: string, player: string, state: KIState) => {
    const key = `${kiKeyStr}-${player}`;
    setKiState(prev => ({ ...prev, [key]: state }));
    // Reconcile with what the server actually stored — its guards may have
    // kept a different acquired_at / needs_run than we optimistically wrote
    // (e.g. an in-flight countdown this tab didn't know about survives the
    // write, and the display must show it rather than silently dropping it).
    const res = await api<{ ok: boolean; state?: Record<string, KIState> }>(
      `ki/${kiKeyStr}/${player}`,
      { method: "PUT", body: JSON.stringify(state) }
    );
    if (res.state) setKiState(prev => ({ ...prev, ...res.state }));
  }, []);

  const toggleStep = useCallback(async (enm: ENM, stepId: string, player: string) => {
    const key = kiKey(enm);
    const ks = getKS(key, player);
    const isLast = enm.kiSteps[enm.kiSteps.length - 1].id === stepId;
    const newSteps = { ...ks.steps, [stepId]: !ks.steps[stepId] };

    if (isLast && !ks.steps[stepId]) {
      // Checking last step → acquire KI. For "acquisition"-triggered ENMs
      // (Pulling the Plug, Fire in the Sky) the NPC won't hand you another
      // KI for 5 days, so the cooldown anchors here. For "run"-triggered
      // ENMs (Sheep, Holy Cow, Brothers) the cooldown anchors on fight
      // completion — leave acquiredAt null; ki/consume sets it when the
      // run is logged.
      const acquiredAt = enm.cooldownTrigger === "run" ? null : new Date().toISOString();
      await updateKI(key, player, { steps: newSteps, acquiredAt, needsRun: true });
    } else if (isLast && ks.steps[stepId]) {
      // Unchecking last step. UI guards block this while holding KI or in
      // cooldown, so we only reach here after a cooldown has naturally
      // expired (e.g. Spent KI ran out). Preserve acquiredAt — nothing
      // resets the countdown except it counting down — and just toggle the
      // step + clear needsRun.
      await updateKI(key, player, { ...ks, steps: newSteps, needsRun: false });
    } else {
      // Non-final step
      await updateKI(key, player, { ...ks, steps: newSteps });
    }
  }, [getKS, updateKI]);

  const spentKI = useCallback(async (enm: ENM, player: string) => {
    const allSteps: Record<string, boolean> = {};
    for (const s of enm.kiSteps) allSteps[s.id] = true;
    await updateKI(kiKey(enm), player, { steps: allSteps, acquiredAt: new Date().toISOString(), needsRun: false });
  }, [updateKI]);

  return { kiState, loaded, getKS, toggleStep, spentKI, refresh };
}

export function usePrices() {
  const [prices, setPrices] = useState<Record<string, PriceEntry>>({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    const data = await api<Record<string, PriceEntry>>("prices");
    setPrices(data);
    setLoaded(true);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const updatePrice = useCallback(async (itemName: string, price: number) => {
    setPrices(prev => ({ ...prev, [itemName]: { price, updated: new Date().toISOString() } }));
    await api(`prices/${encodeURIComponent(itemName)}`, {
      method: "PUT",
      body: JSON.stringify({ price }),
    });
  }, []);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshResults, setRefreshResults] = useState<Record<string, { price: number } | { error: string }> | null>(null);
  const didAutoRefresh = useRef(false);

  // All unique sellable item names across all ENMs
  const allItemNames = useRef(
    [...new Set(ENM_DATA.flatMap(e => e.dropPools.flatMap(p => p.items.map(i => i.sell || i.name))))]
  );

  // Refresh in small chunks — one prices/refresh invocation per ~8 items.
  // Each item costs the Pages function an upstream fetch plus a batch of D1
  // writes, so a full-roster refresh (63 items since the spire ENMs landed)
  // in a single invocation blows Cloudflare's per-request subrequest budget
  // and the fetch dies mid-flight ("Load failed"). Chunks keep every
  // invocation far under the ceiling; results merge across chunks and
  // partial progress is kept if a later chunk fails.
  const refreshFromAH = useCallback(async (itemNames: string[]) => {
    setRefreshing(true);
    setRefreshError(null);
    const CHUNK = 8;
    const merged: Record<string, any> = {};
    try {
      for (let i = 0; i < itemNames.length; i += CHUNK) {
        const res = await api<{ ok: boolean; results: Record<string, any> }>("prices/refresh", {
          method: "POST",
          body: JSON.stringify({ items: itemNames.slice(i, i + CHUNK) }),
        });
        Object.assign(merged, res.results || {});
        setRefreshResults({ ...merged });
      }
    } catch (e: any) {
      setRefreshError(e?.message || "AH refresh failed");
      setRefreshResults(Object.keys(merged).length ? { ...merged } : null);
    } finally {
      await refresh();
      setRefreshing(false);
    }
  }, [refresh]);

  // Auto-refresh from AH once on first page load
  useEffect(() => {
    if (loaded && !didAutoRefresh.current) {
      didAutoRefresh.current = true;
      refreshFromAH(allItemNames.current);
    }
  }, [loaded, refreshFromAH]);

  return { prices, loaded, updatePrice, refresh, refreshFromAH, refreshing, refreshError, refreshResults };
}

// ─── Identity ────────────────────────────────────────────────────────────────

export function useIdentity() {
  const [player, setPlayer] = useState<Player>("Jezza");
  const [detected, setDetected] = useState(false);

  useEffect(() => {
    api<{ player: string | null; authenticated: boolean }>("identity")
      .then(res => {
        if (res.player && PLAYERS.includes(res.player as Player)) {
          setPlayer(res.player as Player);
          setDetected(true);
        }
      })
      .catch(() => {
        // Zero Trust not available (local dev) — default to Jezza
      });
  }, []);

  return { player, setPlayer, detected };
}

// ─── Realtime presence ───────────────────────────────────────────────────────

type RtEvent =
  | { type: "hello"; you: Player; peers: Player[] }
  | { type: "presence:state"; entries: Array<{ player: Player; kind: "drop" | "sale"; enm_id: string; started_at: string }> }
  | { type: "data:drop"; drop: Drop }
  | { type: "data:sale"; sale: Sale }
  | { type: "data:delete"; kind: "drop" | "sale"; id: string }
  // Canonical post-write ki_state rows, keyed `${cdGroup}-${player}`. Sent
  // after every ki PUT and ki/consume so all open tabs converge on what the
  // server actually stored.
  | { type: "data:ki"; state: Record<string, KIState> };

type PresenceEntries = Extract<RtEvent, { type: "presence:state" }>["entries"];
type Listener = (e: RtEvent) => void;
type ConnListener = (connected: boolean) => void;
type PresenceListener = (entries: PresenceEntries) => void;

let socket: WebSocket | null = null;
let backoffMs = 500;
const listeners = new Set<Listener>();
const connListeners = new Set<ConnListener>();
const presenceListeners = new Set<PresenceListener>();
let lastPresence: PresenceEntries = [];
let connected = false;
let outboundQueue: string[] = [];
let currentPlayer: Player | null = null;

function setConnected(value: boolean) {
  if (connected === value) return;
  connected = value;
  for (const l of connListeners) l(value);
}

function setPresence(entries: PresenceEntries) {
  lastPresence = entries;
  for (const l of presenceListeners) l(entries);
}

// Same origin everywhere: in production and under `bun run dev`, /realtime/* reaches the
// rt Worker through the REALTIME service binding.
function rtUrl(player: Player): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/realtime/ws?player=${encodeURIComponent(player)}`;
}

function ensureConnection(player: Player) {
  currentPlayer = player;
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;
  const ws = new WebSocket(rtUrl(player));
  socket = ws;

  ws.addEventListener("open", () => {
    setConnected(true);
    backoffMs = 500;
    for (const m of outboundQueue) ws.send(m);
    outboundQueue = [];
  });
  ws.addEventListener("message", (ev: MessageEvent) => {
    try {
      const data: RtEvent = JSON.parse(ev.data as string);
      if (data.type === "presence:state") setPresence(data.entries);
      for (const l of listeners) l(data);
    } catch { /* ignore */ }
  });
  ws.addEventListener("close", () => {
    setConnected(false);
    socket = null;
    setTimeout(() => { if (currentPlayer) ensureConnection(currentPlayer); }, backoffMs);
    backoffMs = Math.min(backoffMs * 2, 30000);
  });
  ws.addEventListener("error", () => { try { ws.close(); } catch {} });
}

function rtSend(msg: object) {
  const s = JSON.stringify(msg);
  if (socket && socket.readyState === WebSocket.OPEN) socket.send(s);
  else outboundQueue.push(s);
}

// ── Focused hooks ──
//
// Each hook subscribes to exactly one slice of realtime state, so a component
// reading `connected` doesn't re-render when `presence` changes (and vice
// versa). Mount `useRealtimeReady` once at the App root to open the
// connection; leaf components then pick the slice they need.

/** Opens the WS singleton. Mount once at the App root. Returns nothing. */
export function useRealtimeReady(player: Player): void {
  useEffect(() => { ensureConnection(player); }, [player]);
}

export function useRealtimeConnected(): boolean {
  const [conn, setConn] = useState(connected);
  useEffect(() => {
    const l: ConnListener = (v) => setConn(v);
    connListeners.add(l);
    setConn(connected);
    return () => { connListeners.delete(l); };
  }, []);
  return conn;
}

export function useRealtimePresence(): PresenceEntries {
  const [pres, setPres] = useState(lastPresence);
  useEffect(() => {
    const l: PresenceListener = (e) => setPres(e);
    presenceListeners.add(l);
    setPres(lastPresence);
    return () => { presenceListeners.delete(l); };
  }, []);
  return pres;
}

const stableSendPresence = (kind: "drop" | "sale", enmId: string): void => {
  rtSend({ type: "presence:open", kind, enm_id: enmId });
};
const stableClearPresence = (): void => {
  rtSend({ type: "presence:close" });
};

export function useRealtimeActions() {
  // Both functions are module-level constants — same identity every render,
  // so consumers can include them in deps arrays without churn.
  return { sendPresence: stableSendPresence, clearPresence: stableClearPresence };
}

/**
 * Convenience composition of the four focused hooks. Re-renders on any
 * realtime state change. Prefer the focused hooks in new code; this one
 * exists for tests and call sites that legitimately need everything.
 */
export function useRealtime(player: Player) {
  useRealtimeReady(player);
  const presence = useRealtimePresence();
  const conn = useRealtimeConnected();
  const { sendPresence, clearPresence } = useRealtimeActions();
  return { presence, connected: conn, sendPresence, clearPresence };
}

export function subscribeRealtime(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function subscribeRealtimeConn(listener: ConnListener): () => void {
  connListeners.add(listener);
  return () => { connListeners.delete(listener); };
}

export function _resetRealtimeForTests() {
  if (socket) { try { socket.close(); } catch {} }
  socket = null;
  backoffMs = 500;
  outboundQueue = [];
  listeners.clear();
  connListeners.clear();
  presenceListeners.clear();
  lastPresence = [];
  connected = false;
  currentPlayer = null;
}
